import { useState, useRef } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import {
  UploadIcon,
  CheckIcon,
  AlertCircleIcon,
  TrashIcon,
  EyeIcon,
} from "lucide-react";
import { Loader } from "components/Loader";
import { toast } from "Layout/slice";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  VerificationModalContainer,
  VerificationUploadArea,
  FilePreviewContainer,
  ProcessingStateContainer,
  ResultStateContainer,
  ActionButtonWrapper,
  RequirementsBox,
  StyledParagraph,
  FileListWrapper,
} from "./IdentityVerificationModal.styles";
import { uploadAccountDocument } from "features/Account/service";
import {
  ALLOWED_ALL_TYPES,
  DOCUMENT_CONFIG,
  DOCUMENT_TYPES,
  MAX_SIZE,
  MODAL_STATUS,
} from "./constants";
import { formatFileSize } from "utils/index";
import { ALPACA_ACCOUNT_STATUS_KEY } from "features/Account/hook";

export const IdentityVerificationModal = ({
  show,
  onHide,
  onSuccess,
  requiredDocumentType,
}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState(MODAL_STATUS.UPLOAD); // upload | processing | error | success
  const [errorMessage, setErrorMessage] = useState("");
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);

  const activeConfig =
    DOCUMENT_CONFIG[requiredDocumentType] ||
    DOCUMENT_CONFIG[DOCUMENT_TYPES.IDENTITY_VERIFICATION];

  const ALLOWED_TYPES = activeConfig.allowedFileTypes || ALLOWED_ALL_TYPES;

  const mutation = useMutation({
    mutationFn: (payload) => uploadAccountDocument(payload),

    onSuccess: (data) => {
      setStatus(
        data?.status === 202
          ? MODAL_STATUS.SUCCESS_PROCESSING
          : MODAL_STATUS.SUCCESS
      );
      queryClient.invalidateQueries([ALPACA_ACCOUNT_STATUS_KEY]);
    },

    onError: (err) => {
      const msg =
        err?.response?.data?.error?.message ||
        err?.message ||
        "Failed to upload document";
      setErrorMessage(msg);
      dispatch(
        toast({
          type: "error",
          body: msg,
        })
      );
      setStatus(MODAL_STATUS.ERROR);
    },
  });

  const { mutateAsync: uploadDocumentAsync, isPending: isLoading } = mutation;

  const getFormatttedAllowedNames = () => {
    const extensions = new Set();
    ALLOWED_TYPES.forEach((type) => {
      if (type.includes("pdf")) extensions.add("PDF");
      else if (type.includes("image")) extensions.add("JPG, PNG");
    });
    return Array.from(extensions).join(", ");
  };

  const reset = () => {
    setFile(null);
    setErrorMessage("");
    setStatus(MODAL_STATUS.UPLOAD);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    reset();
    onHide();
    if (
      status === MODAL_STATUS.SUCCESS ||
      status === MODAL_STATUS.SUCCESS_PROCESSING
    ) {
      onSuccess?.();
    }
  };

  const handleFileSelect = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    if (!ALLOWED_TYPES.includes(selected.type)) {
      dispatch(
        toast({
          type: "error",
          body: `Only ${getFormatttedAllowedNames()} files allowed.`,
        })
      );
      return;
    }

    if (selected.size > MAX_SIZE) {
      dispatch(
        toast({
          type: "error",
          body: "File size must not exceed 10MB.",
        })
      );
      return;
    }

    setFile(selected);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = () => setFile(null);

  const handlePreview = (f) => {
    const url = URL.createObjectURL(f);
    window.open(url, "_blank");
  };

  const handleUpload = async () => {
    if (!file) return;

    setStatus(MODAL_STATUS.PROCESSING);

    try {
      const formData = new FormData();
      formData.append("document", file);
      if (requiredDocumentType) {
        formData.append("document_type", requiredDocumentType);
      }

      await uploadDocumentAsync(formData);
    } catch (error) {
      console.error("Upload preparation failed", error);
      dispatch(
        toast({
          type: "error",
          body: "Failed to upload document",
        })
      );
      setStatus(MODAL_STATUS.ERROR);
    }
  };

  const renderContent = () => {
    if (status === MODAL_STATUS.PROCESSING) {
      return (
        <ProcessingStateContainer>
          <div className="spinner">
            <Loader size="lg" />
          </div>
          <h3>Processing ID</h3>
          <p>Please wait while we process your upload…</p>
        </ProcessingStateContainer>
      );
    }

    if (status === MODAL_STATUS.ERROR) {
      return (
        <ResultStateContainer>
          <div className="icon-wrapper error">
            <AlertCircleIcon size={28} />
          </div>
          <h3>Upload Failed</h3>
          <p>
            {errorMessage ||
              "Please try again with a clearer or different document."}
          </p>

          <div className="action-buttons">
            <button className="cancel" onClick={handleClose}>
              Cancel
            </button>
            <button
              className="primary"
              onClick={() => setStatus(MODAL_STATUS.UPLOAD)}
            >
              Try again
            </button>
          </div>
        </ResultStateContainer>
      );
    }

    if (
      status === MODAL_STATUS.SUCCESS ||
      status === MODAL_STATUS.SUCCESS_PROCESSING
    ) {
      return (
        <ResultStateContainer>
          <div className="icon-wrapper success">
            <CheckIcon size={28} />
          </div>
          <h3>Congratulations!</h3>
          <p>
            {status === MODAL_STATUS.SUCCESS_PROCESSING
              ? "Your document has been received and is being processed."
              : "Your document has been successfully uploaded."}
          </p>

          <div className="action-buttons">
            <button className="primary" onClick={handleClose}>
              Done
            </button>
          </div>
        </ResultStateContainer>
      );
    }

    return (
      <>
        <div className="heading">
          <h2>{activeConfig.title}</h2>
          <StyledParagraph>{activeConfig.description}</StyledParagraph>
        </div>

        <RequirementsBox>
          <ul>
            {activeConfig.requirements.map((req, index) => (
              <li key={index}>{req}</li>
            ))}
          </ul>
        </RequirementsBox>

        {!file ? (
          <VerificationUploadArea onClick={() => fileInputRef.current?.click()}>
            <div className="upload-icon-wrapper">
              <UploadIcon size={20} />
            </div>
            <div className="upload-text">
              <span>Click to upload</span> or drag & drop
            </div>
            <p className="sub-title">
              {getFormatttedAllowedNames()} (max 10MB)
            </p>

            <input
              type="file"
              ref={fileInputRef}
              accept={ALLOWED_TYPES.join(",")}
              className="d-none"
              onChange={handleFileSelect}
            />
          </VerificationUploadArea>
        ) : (
          <FileListWrapper>
            <FilePreviewContainer>
              <div className="file-info">
                <div className="file-icon">
                  {file.name.split(".").pop().toUpperCase()}
                </div>

                <div className="file-details">
                  <span className="file-name">{file.name}</span>
                  <span className="file-size-status">
                    {formatFileSize(file.size)}
                    <span className="success-text">
                      <CheckIcon size={12} color="green" /> 100%
                    </span>
                  </span>
                </div>
              </div>

              <div className="actions">
                <EyeIcon
                  size={16}
                  className="action-btn"
                  onClick={() => handlePreview(file)}
                />
                <TrashIcon
                  size={16}
                  className="action-btn delete"
                  onClick={removeFile}
                />
              </div>
            </FilePreviewContainer>
          </FileListWrapper>
        )}

        <ActionButtonWrapper>
          <button className="btn" onClick={handleClose}>
            Cancel
          </button>
          <button
            className="btn-submit"
            onClick={handleUpload}
            disabled={!file || isLoading}
          >
            {isLoading ? "Uploading…" : "Upload"}
          </button>
        </ActionButtonWrapper>
      </>
    );
  };

  return (
    <VerificationModalContainer show={show} onHide={handleClose} centered>
      {renderContent()}
    </VerificationModalContainer>
  );
};

IdentityVerificationModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  requiredDocumentType: PropTypes.string,
};
